import { useState } from 'react';
import { Link, Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext.jsx';
import { clearJwt } from '../api/client.js';
import { AnimatedBackground } from './AnimatedBackground.jsx';

export function AppShell({ user, variant = 'app', children }) {
  const navigate = useNavigate();
  const { scheme, toggle } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);

  async function logout() {
    clearJwt();
    navigate('/login');
    window.location.reload();
  }

  const linkCls = ({ isActive }) =>
    `rounded-xl px-3 py-2 text-sm font-semibold tracking-tight transition ${
      isActive
        ? 'bg-accent/15 text-accent shadow-sm dark:bg-white/10 dark:text-mist'
        : 'text-ink/75 hover:bg-black/5 dark:text-mist/75 dark:hover:bg-white/5'
    }`;

  const isAdmin = user?.role === 'super_admin';

  return (
    <div className="relative isolate min-h-screen bg-gradient-to-br from-white via-[#eef1eb] to-[#dfe7dd] pb-24 text-ink dark:from-[#050608] dark:via-[#0b1014] dark:to-[#08121a] dark:text-mist">
      <AnimatedBackground />
      <header className="sticky top-0 z-20 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-3 px-4 py-4">
          <Link to="/" className="group flex flex-col gap-0.5">
            <span className="font-display text-xl font-semibold tracking-tight">
              <span className="text-gradient">Omin</span>
              <span className="text-gradient-ia">IA</span>
            </span>
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-ink/50 dark:text-frost-muted">
              pedidos SaaS
            </span>
          </Link>

          <div className="ml-auto flex items-center gap-2 md:hidden">
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="rounded-full border border-ink/15 bg-white/90 px-3 py-2 text-xs font-bold uppercase tracking-wide text-ink shadow-sm dark:border-white/15 dark:bg-white/10 dark:text-mist"
              aria-expanded={menuOpen}
              aria-label="Abrir navegação"
            >
              {menuOpen ? 'Fechar' : 'Menu'}
            </button>
            <button
              type="button"
              onClick={logout}
              className="rounded-full bg-ink px-3 py-2 text-xs font-semibold uppercase tracking-wide text-mist dark:bg-mist dark:text-ink"
            >
              Sair
            </button>
          </div>

          <nav
            className={`order-3 w-full flex-wrap gap-2 rounded-2xl border border-ink/10 bg-white/85 p-2 shadow-sm dark:border-white/10 dark:bg-white/5 md:order-none md:flex md:w-auto md:flex-1 md:border-0 md:bg-transparent md:p-0 md:shadow-none ${
              menuOpen ? 'flex' : 'hidden'
            }`}
          >
            {variant === 'app' && (
              <>
                <NavLink to="/app/dashboard" end className={linkCls} onClick={() => setMenuOpen(false)}>
                  Dashboard
                </NavLink>
                <NavLink to="/app/empresa" className={linkCls} onClick={() => setMenuOpen(false)}>
                  Empresas
                </NavLink>
                <NavLink to="/app/mensalidade" className={linkCls} onClick={() => setMenuOpen(false)}>
                  Mensalidade
                </NavLink>
                <NavLink to="/app/produtos" className={linkCls} onClick={() => setMenuOpen(false)}>
                  Produtos
                </NavLink>
                <NavLink to="/app/secoes" className={linkCls} onClick={() => setMenuOpen(false)}>
                  Secções
                </NavLink>
                <NavLink to="/app/pedidos" className={linkCls} onClick={() => setMenuOpen(false)}>
                  Pedidos
                </NavLink>
                <NavLink to="/app/clientes" className={linkCls} onClick={() => setMenuOpen(false)}>
                  Clientes
                </NavLink>
                <NavLink to="/app/radar" className={linkCls} onClick={() => setMenuOpen(false)}>
                  Radar
                </NavLink>
              </>
            )}
            {variant === 'admin' && (
              <>
                <NavLink to="/admin" end className={linkCls} onClick={() => setMenuOpen(false)}>
                  Visão geral
                </NavLink>
                <NavLink to="/app/mensalidade" className={linkCls} onClick={() => setMenuOpen(false)}>
                  Mensalidade
                </NavLink>
                <NavLink to="/app/dashboard" className={linkCls} onClick={() => setMenuOpen(false)}>
                  Voltar ao painel
                </NavLink>
              </>
            )}
            {isAdmin && variant === 'app' && (
              <NavLink to="/admin" className={linkCls} onClick={() => setMenuOpen(false)}>
                Admin global
              </NavLink>
            )}
          </nav>

          <div className="ml-auto hidden items-center gap-2 md:flex">
            <button
              type="button"
              onClick={toggle}
              className="rounded-full border border-ink/10 bg-white/80 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-ink shadow-sm backdrop-blur dark:border-white/10 dark:bg-white/5 dark:text-mist"
            >
              {scheme === 'dark' ? 'Claro' : 'Escuro'}
            </button>
            <button
              type="button"
              onClick={logout}
              className="rounded-full bg-ink px-3 py-2 text-xs font-semibold uppercase tracking-wide text-mist dark:bg-mist dark:text-ink"
            >
              Sair
            </button>
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-6xl px-4">{children ?? <Outlet />}</main>
    </div>
  );
}
